import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

import { boundToYmd } from './dateRangeBound'

/**
 * Generic date-range facet bound to a store path holding `{gte?, lte?}` ISO
 * strings — the shape the backend's `range` filter understands (see
 * ElasticsearchEndpointController::buildUserFilterClause).
 *
 * Two `<input type="date">`: the lower bound is stamped to the start of the day
 * (`T00:00:00`) and the upper to the end (`T23:59:59.999`) so the range is
 * inclusive of both endpoints. An empty input drops its bound; clearing both
 * writes `{}` (no clause). Like the other kit facets the refetch lives in the
 * YAML `actions:` (fired `on: change`) and runs on the bubbled event.
 *
 * YAML usage:
 *   - type: DateRangeFacet
 *     dataLocation: ~~.selectedFilters.destination_response_start_time
 *     actions:
 *       - { what: setData, on: change, path: ~~.pagination.from, value: 0 }
 *       - { what: submitData, on: change, ... }
 */

export const DateRangeFacet = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const fromSuffix = resolve(props.fromSuffix) ?? 'T00:00:00'
  const toSuffix = resolve(props.toSuffix) ?? 'T23:59:59.999'
  const inputClass = resolve(props.inputClass)
    ?? 'border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: {},
    globalDataContext,
    templateContext,
  })
  const range = formData && typeof formData === 'object' ? formData : {}

  // ISO string → YYYY-MM-DD for the native date input.
  // ISO ou date-math (`now-30d`) → YYYY-MM-DD, cf. dateRangeBound.js.
  const ymd = (value) => boundToYmd(value)
  const fromVal = ymd(range.gte)
  const toVal = ymd(range.lte)

  const write = (fromYmd, toYmd) => {
    const next = {}
    if (fromYmd) next.gte = `${fromYmd}${fromSuffix}`
    if (toYmd) next.lte = `${toYmd}${toSuffix}`
    globalDataContext.updateData(next, formDataPath)
  }

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef} className="flex items-center gap-2">
        <input
          type="date"
          aria-label={resolve(props.fromLabel) ?? 'Début de la période'}
          value={fromVal}
          max={toVal || undefined}
          onChange={(e) => write(e.target.value, toVal)}
          className={inputClass}
        />
        <span className="text-gray-400 text-sm">→</span>
        <input
          type="date"
          aria-label={resolve(props.toLabel) ?? 'Fin de la période'}
          value={toVal}
          min={fromVal || undefined}
          onChange={(e) => write(fromVal, e.target.value)}
          className={inputClass}
        />
      </div>
    </ActionDependant>
  )
}
